import React, { useEffect, useState } from 'react';
import { Mail, Phone, CheckCircle, Clock, XCircle, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const token = localStorage.getItem('adminToken');

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/contact`, { headers })
      .then(res => setContacts(res.data))
      .catch(() => toast.error('Fehler beim Laden'))
      .finally(() => setLoading(false));
  }, [token]);

  const updateStatus = async (id, status) => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.put(`${API}/contact/${id}?status=${status}`, {}, { headers });
      toast.success('Status aktualisiert!');
      // Refresh contacts
      axios.get(`${API}/contact`, { headers })
        .then(res => setContacts(res.data))
        .catch(() => toast.error('Fehler beim Laden'));
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const deleteContact = async (id) => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.delete(`${API}/contact/${id}`, { headers });
      toast.success('Anfrage gelöscht!');
      setContacts(contacts.filter(c => c.id !== id));
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const filteredContacts = contacts.filter(c => {
    if (filter === 'all') return true;
    return c.status === filter;
  });

  const statusOptions = [
    { value: 'new', label: 'Neu', icon: Clock, color: 'blue' },
    { value: 'contacted', label: 'Kontaktiert', icon: Phone, color: 'yellow' },
    { value: 'done', label: 'Erledigt', icon: CheckCircle, color: 'green' },
    { value: 'cancelled', label: 'Abgebrochen', icon: XCircle, color: 'red' },
  ];

  if (loading) {
    return <div className="flex justify-center py-12"><div className="spinner"></div></div>;
  }

  return (
    <div data-testid="admin-contacts">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Kontaktanfragen</h1>
          <p className="text-slate-600">Verwalte eingehende Anfragen von Interessenten.</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle ({contacts.length})</SelectItem>
            <SelectItem value="new">Neu ({contacts.filter(c => c.status === 'new').length})</SelectItem>
            <SelectItem value="contacted">Kontaktiert ({contacts.filter(c => c.status === 'contacted').length})</SelectItem>
            <SelectItem value="done">Erledigt ({contacts.filter(c => c.status === 'done').length})</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {statusOptions.map((status) => {
          const count = contacts.filter(c => c.status === status.value).length;
          return (
            <Card key={status.value} className={filter === status.value ? 'ring-2 ring-blue-500' : ''}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg bg-${status.color}-100 flex items-center justify-center`}>
                    <status.icon className={`w-5 h-5 text-${status.color}-600`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{count}</p>
                    <p className="text-sm text-slate-500">{status.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Contact List */}
      <div className="space-y-4">
        {filteredContacts.map((contact) => (
          <Card key={contact.id} data-testid={`contact-item-${contact.id}`}>
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-slate-900">{contact.name}</h3>
                    <span className={`badge ${
                      contact.status === 'new' ? 'badge-info' : 
                      contact.status === 'contacted' ? 'badge-warning' : 
                      contact.status === 'done' ? 'badge-success' : 'badge-error'
                    }`}>
                      {statusOptions.find(s => s.value === contact.status)?.label}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-slate-600">
                    <a href={`mailto:${contact.email}`} className="flex items-center gap-2 hover:text-blue-600">
                      <Mail className="w-4 h-4" />
                      {contact.email}
                    </a>
                    <a href={`tel:${contact.phone}`} className="flex items-center gap-2 hover:text-blue-600">
                      <Phone className="w-4 h-4" />
                      {contact.phone}
                    </a>
                    <span>Klasse {contact.license_type} • {contact.location || 'Kein Standort'}</span>
                  </div>
                  {contact.message && (
                    <p className="mt-3 text-slate-600 bg-slate-50 p-3 rounded-lg">
                      {contact.message}
                    </p>
                  )}
                  <p className="text-sm text-slate-400 mt-2">
                    Eingegangen am {new Date(contact.created_at).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                <div className="flex gap-2 items-center">
                  <Select 
                    value={contact.status} 
                    onValueChange={(value) => updateStatus(contact.id, value)}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((status) => (
                        <SelectItem key={status.value} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Anfrage löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Möchten Sie die Anfrage von "{contact.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => deleteContact(contact.id)}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Löschen
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredContacts.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            Keine Anfragen gefunden.
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminContacts;
